import simpleRestProvider from 'ra-data-simple-rest';
import { fetchUtils } from 'react-admin';
import queryString from 'query-string';
import { UserProfile } from '../customRoutes/authentication/types';
import { DataProviderWithCustomMethods } from '../types';
import { API_URL } from '../constants';
import { logEvent } from '../analytics';
import { authProvider } from '../authProvider';

export const fetchJson = async (
  url: string,
  options: fetchUtils.Options = {}
) => {
  const accessToken = await authProvider.getAccessToken();
  if (!accessToken) {
    return Promise.reject('No access token available');
  }

  const customHeaders = (options.headers ||
    new Headers({
      Accept: 'application/json',
    })) as Headers;

  customHeaders.set('Authorization', `Bearer ${accessToken}`);
  options.headers = customHeaders;

  let requestUrl = url;
  if (!requestUrl.startsWith('http')) {
    requestUrl = API_URL + requestUrl;
  }

  const urlObject = new URL(requestUrl);
  const publisherId =
    window.location.pathname.match(/\/publisher\/([^/]+)/)?.[1] ??
    (() => {
      try {
        return (
          localStorage.getItem('gl:publisher_id') ??
          (JSON.parse(
            localStorage.getItem('RaStore.publisher_id') ?? 'null'
          ) as string | null) ??
          undefined
        );
      } catch {
        return undefined;
      }
    })();
  if (publisherId) {
    urlObject.searchParams.set('publisher_id', publisherId);
  }

  return fetchUtils.fetchJson(urlObject.toString(), options);
};

const simpleRestDataProvider: DataProviderWithCustomMethods = {
  ...simpleRestProvider(API_URL, fetchJson),
  getList: async (resource: string, params: any) => {
    const defaultSort = ['created_at', 'DESC'];
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const query = {
      // It doesn't make sens to sort by ID since we use UUID, so override the sort with ['created_at', 'DESC']
      sort:
        field === 'id'
          ? JSON.stringify(defaultSort)
          : JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify(params.filter),
    };
    const url = `${API_URL}/${resource}?${queryString.stringify(query)}`;
    const { json, headers } = await fetchJson(url, {
      signal: params.signal,
    });
    return {
      data: json,
      total: parseInt(
        headers.get('content-range')?.split('/').pop() || '0',
        10
      ),
    };
  },
  previewBlob: async (url, data) => {
    const accessToken = await authProvider.getAccessToken();
    if (!accessToken) {
      return Promise.reject('No access token available');
    }
    const headers = new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }) as Headers;

    headers.set('Authorization', `Bearer ${accessToken}`);

    const response = await fetch(API_URL + url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(`${errorData.message || 'Something went wrong'}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl);
  },
};

export const getDataProvider = async () => {
  if (import.meta.env.VITE_FAKE_DATA_PROVIDER_ENABLED) {
    const { fakeDataProvider } = await import('./fakeDataProvider');
    return fakeDataProvider;
  }
  return simpleRestDataProvider;
};

export const getDashboardPermissions = async () => {
  const { json } = await fetchJson(`${API_URL}/dashboard-permissions`, {
    method: 'GET',
  });
  return json;
};

export const getUserProfile = async () => {
  const { json } = await fetchJson(`${API_URL}/profile`, {
    method: 'GET',
  });
  return json;
};

export const createUserProfile = async (payload: UserProfile) => {
  await fetchJson(`${API_URL}/publisher-users`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  logEvent('profile-created');
};

export const updateOfferNegotiation = async (
  action: string,
  data?: Record<string, any>
) => {
  const { json } = await fetchJson(`${API_URL}/offer-negotiations/${action}`, {
    method: 'POST',
    body: JSON.stringify({ ...data, action_type: action }),
    headers: new Headers({ 'Content-Type': 'application/json' }),
  });

  return json;
};
